import React, { useState, useEffect } from 'react';
import { FiArrowLeft, FiRefreshCw, FiAlertCircle, FiCheckCircle, FiNavigation, FiCircle, FiInfo, FiCalendar } from 'react-icons/fi';
import { FaTrain } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../components/Common/Card';
import Button from '../components/Common/Button';
import Shimmer from '../components/Common/Shimmer';
import apiService from '../services/api';
import styles from './TrainStatusResultPage.module.css';
import { i } from 'framer-motion/dist/types.d-BJcRxCew';

interface StationData {
  index: number;
  station: string;
  arr: string;
  dep: string;
  delay: string;
  status: 'crossed' | 'upcoming';
  current: string;
}

const TrainStatusResultPage: React.FC = () => {
  const { trainNumber, date } = useParams<{ trainNumber: string; date: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [trainData, setTrainData] = useState<StationData[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (trainNumber && date) {
      fetchTrainStatus(trainNumber, date);
    } else {
      navigate('/train-status');
    }
  }, [trainNumber, date]);

  const fetchTrainStatus = async (trainNo: string, journeyDate: string) => {
    setIsLoading(true);
    setError(null);
    setTrainData(null);

    try {
      const response = await apiService.trackTrain(trainNo, journeyDate);

      if (response.success && response.data) {
        const stations = Array.isArray(response.data) ? response.data : [];
        
        if (stations.length > 0) {
          setTrainData(stations);
          toast.success('Train status fetched successfully!');
        } else {
          setError('No station data available');
          toast.error('No station data available');
        }
      } else {
        setError(response.error || 'Failed to fetch train status');
        toast.error(response.error || 'Failed to fetch train status');
      }
    } catch (error) {
      setError('An error occurred. Please try again.');
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    if (trainNumber && date) {
      fetchTrainStatus(trainNumber, date);
    }
  };

  const handleNewSearch = () => {
    navigate('/train-status');
  };

  // Date Navigation Functions
  const formatDateToDDMMYYYY = (dateObj: Date): string => {
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const parseDateFromDDMMYYYY = (dateStr: string): Date => {
    const [day, month, year] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const getDateOffset = (offset: number): string => {
    if (!date) return '';
    const currentDate = parseDateFromDDMMYYYY(date);
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + offset);
    return formatDateToDDMMYYYY(newDate);
  };

  const handleDateNavigation = (offset: number) => {
    if (!trainNumber) return;
    const newDate = getDateOffset(offset);
    navigate(`/train-status/${trainNumber}/${newDate}`);
  };

  const isDateActive = (offset: number): boolean => {
    if (!date) return false;
    const targetDate = getDateOffset(offset);
    return targetDate === date;
  };

  const getDateLabel = (offset: number): string => {
    const targetDate = getDateOffset(offset);
    const [day, month, year] = targetDate.split('-');
    const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short' };
    return dateObj.toLocaleDateString('en-IN', options);
  };

  const parseDelay = (delayStr: string): number | null => {
    if (!delayStr || delayStr === '') return null;
    const match = delayStr.match(/(\d+)min/);
    return match ? parseInt(match[1]) : null;
  };

  const findCurrentIndex = (): number => {
    if (!trainData) return -1;
    const currentStation = trainData.find(s => s.current === 'true');
    return currentStation ? currentStation.index : -1;
  };

  const currentIndex = findCurrentIndex();

  return (
    <div className={styles.trainStatusResultPage}>
      <div className="container">
        {/* Header with Date Navigation */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerLeft}>
              <h1 className={styles.title}>
                <FaTrain className={styles.titleIcon} />
                Train Running Status
              </h1>
              <p className={styles.subtitle}>Train {trainNumber}</p>
            </div>
            <div className={styles.headerActions}>
              <Button variant="outline" size="md" onClick={handleNewSearch}>
                <FiArrowLeft />
                New Search
              </Button>
              <Button variant="secondary" size="md" onClick={handleRefresh} isLoading={isLoading}>
                <FiRefreshCw />
                Refresh
              </Button>
            </div>
          </div>

          {/* Date Navigation Buttons */}
          <div className={styles.dateNavigation}>
            <div className={styles.dateNavLabel}>
              <FiCalendar />
              <span>Journey Date:</span>
            </div>
            <div className={styles.compactDateButtons}>
              <button
                className={`${styles.arrowButton}`}
                onClick={() => handleDateNavigation(-1)}
                disabled={isLoading}
                aria-label="Previous Day"
              >
                ←
              </button>
              
              <button
                className={`${styles.dateButton} ${styles.today} ${isDateActive(0) ? styles.active : ''}`}
                onClick={() => handleDateNavigation(0)}
                disabled={isLoading}
              >
                <span className={styles.dateButtonDate}>{getDateLabel(0)}</span>
              </button>

              <button
                className={`${styles.arrowButton}`}
                onClick={() => handleDateNavigation(1)}
                disabled={isLoading}
                aria-label="Next Day"
              >
                →
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className={styles.resultsSection}>
            <Shimmer type="card" count={1} />
            <Shimmer type="table" count={1} />
          </div>
        )}

        {/* Error State */}
        {!isLoading && error && (
          <Card className={styles.errorCard}>
            <div className={styles.errorContent}>
              <FiAlertCircle className={styles.errorIcon} />
              <h3 className={styles.errorTitle}>Unable to Fetch Train Status</h3>
              <p className={styles.errorMessage}>{error}</p>
              <div className={styles.errorActions}>
                <Button variant="primary" onClick={handleRefresh}>
                  Try Again
                </Button>
                <Button variant="outline" onClick={handleNewSearch}>
                  New Search
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Results - Professional Layout */}
        {!isLoading && !error && trainData && trainData.length > 0 && (
          <div className={styles.resultsSection}>
            {/* Station Timeline Table */}
            <Card className={styles.stationsTableCard}>
              <div className={styles.tableWrapper}>
                {/* Table Header */}
                <div className={styles.tableHeader}>
                  <div className={styles.colStation}>Station</div>
                  <div className={styles.colArrival}>
                    <div className={styles.colTitle}>Arrival Status</div>
                    <div className={styles.colSubtitle}>Actual/Scheduled</div>
                  </div>
                  <div className={styles.colDeparture}>
                    <div className={styles.colTitle}>Departure Status</div>
                    <div className={styles.colSubtitle}>Actual/Scheduled</div>
                  </div>
                  <div className={styles.colDelay}>Delay</div>
                  <div className={styles.colHalt}>PF/Halt</div>
                </div>

                {/* Table Body with Connected Timeline */}
                <div className={styles.tableBody}>
                    {/* Blinking Points */}
                  <div
                    className={styles.redLineBlinkingPoints}
                    style={{
                      height: `${((currentIndex + 1) / trainData.length) * 100}%`,
                      transition: 'height 0.8s ease-in-out',
                    }}
                  >
                    {[...Array(8)].map((_, i) => (
                      <div
                        key={i}
                        className={styles.blinkingPoint}
                        style={{
                          top: `${(i / 7) * 100}%`,
                          animationDelay: `${i * 0.2}s`,
                        }}
                      />
                    ))}
                  </div>

                  {trainData.map((station, index) => {
                    const isCrossed = station.status === 'crossed';
                    const isCurrent = station.current === 'true';
                    const isSource = index === 0;
                    const isDestination = index === trainData.length - 1;
                    const delay = parseDelay(station.delay);

                    return (
                      <div
                        key={station.index}
                        className={`${styles.tableRow} ${isCrossed ? styles.crossed : ''} ${
                          isCurrent ? styles.current : ''
                        }`}
                      >
                        <div className={styles.colStation}>
                          <div className={styles.stationWithTimeline}>
                            <div className={styles.progressTimeline}>
                              <div className={styles.progressDotWrapper}>
                                {isCurrent ? (
                                  <div className={styles.currentDot}>
                                    <div className={styles.currentDotInner}></div>
                                  </div>
                                ) : (
                                  <div
                                    className={`${styles.progressDot} ${
                                      isCrossed ? styles.filled : ''
                                    }`}
                                  ></div>
                                )}
                              </div>
                            </div>

                            <div className={styles.stationInfo}>
                              <div className={styles.stationName}>
                                {station.station}
                                {isSource && <span className={styles.sourceLabel}>Source</span>}
                                {isDestination && <span className={styles.destLabel}>Destination</span>}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className={styles.colArrival}>
                          {station.arr ? (
                            <div className={styles.timeDisplay}>
                              <div className={styles.scheduledTime}>{station.arr}</div>
                            </div>
                          ) : (
                            <div className={styles.sourceText}>Source</div>
                          )}
                        </div>

                        <div className={styles.colDeparture}>
                          <div className={styles.timeDisplay}>
                            {isCrossed && station.dep && (
                              <div className={styles.actualTime}>{station.dep}</div>
                            )}
                            <div className={styles.scheduledTime}>{station.dep}</div>
                          </div>
                        </div>

                        <div className={styles.colDelay}>
                          {delay !== null && delay > 0 ? (
                            <div className={styles.delayBadge}>{delay}min</div>
                          ) : (
                            <div className={styles.noDelay}>--</div>
                          )}
                        </div>

                        <div className={styles.colHalt}>
                          {isSource ? (
                            <div className={styles.sourceTag}>Source</div>
                          ) : (
                            <div className={styles.haltInfo}>
                              <div className={styles.haltText}>--</div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainStatusResultPage;